import React, { DOMAttributes, useRef } from 'react'
import { AriaToastProps, useToast } from '@react-aria/toast'
import { ToastState } from '@react-stately/toast'
import { SvgRemove } from '@chilipiper/icons/src/design-system'
import styled, { keyframes } from 'styled-components'
import { FocusableElement } from '@react-types/shared'
import { AriaButtonProps, mergeProps } from 'react-aria'
import { IconButton } from '../../action'
import { Box, Flex, Text } from '../../../core-components'
import { AlertIcon } from '../../data/alert-icon/AlertIcon'
import { useFocusVisible } from '../../hooks'
import { AlertBanner } from '../alert-banner/AlertBanner'
import { AlertBannerType, ToastType } from './types'

export interface ToastProps extends AriaToastProps<ToastType | AlertBannerType> {
  state: ToastState<ToastType | AlertBannerType>
}

export const Toast = ({ state, toast, ...ariaProps }: ToastProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const { toastProps, titleProps, closeButtonProps, contentProps, descriptionProps } = useToast(
    { toast, ...ariaProps },
    state,
    ref
  )
  const { focusProps, isFocusVisible } = useFocusVisible()
  const { content } = toast

  return (
    <AnimatedWrapperBox>
      <AnimatedFlex
        ref={ref}
        w={content.INTERNAL_type === 'alert' ? '432px' : '386px'}
        data-test-id={toast.content['data-test-id']}
        isFocusVisible={isFocusVisible}
        style={{
          viewTransitionName: `toast-${toast.key}`,
        }}
        {...mergeProps(toastProps, focusProps)}
      >
        {content.INTERNAL_type === 'toast' ? (
          <ToastContent
            variant={content.variant}
            title={content.title}
            titleProps={titleProps}
            contentProps={contentProps}
            closeButtonProps={closeButtonProps}
          />
        ) : (
          <AlertBanner
            type='floating'
            variant={content.variant}
            title={content.title}
            description={content.description}
            actions={content.actions}
            isDismissable={content.isDismissable}
            onClose={() => state.close(toast.key)}
            titleProps={titleProps}
            contentProps={contentProps}
            descriptionProps={descriptionProps}
            closeButtonProps={closeButtonProps}
          />
        )}
      </AnimatedFlex>
    </AnimatedWrapperBox>
  )
}

const ToastContent = ({
  title,
  titleProps,
  closeButtonProps,
  variant,
  contentProps,
}: {
  closeButtonProps: AriaButtonProps<'button'>
  title: string
  titleProps: DOMAttributes<FocusableElement>
  variant: ToastType['variant']
  contentProps: DOMAttributes<FocusableElement>
}) => {
  return (
    <Box bg='bg/accent' borderRadius={8} w='100%' {...contentProps}>
      <Flex px={6} py={4} gap={3}>
        <AlertIcon size={4} variant={variant} mt={1} />
        <StyledText textStyle='body-multiline' color='fg/major-on-color' {...titleProps}>
          {title}
        </StyledText>
        <IconButton
          icon={SvgRemove}
          variant='ghost'
          size={6}
          label='Close'
          hasTooltip={false}
          as='button'
          ariaProps={closeButtonProps}
        />
      </Flex>
    </Box>
  )
}

const slideIn = keyframes`
  from {
    transform: translateY(-200%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const AnimatedWrapperBox = styled(Box)`
  transition: height 0.2s ease-in-out;
  pointer-events: auto;
`

const AnimatedFlex = styled(Flex)<{ isFocusVisible: boolean }>`
  view-transition-class: toast;
  :focus {
    outline: ${({ isFocusVisible }) => (isFocusVisible ? 'revert' : 'none')};
  }
  animation: ${slideIn} 0.2s ease-in-out;
`

const StyledText = styled(Text)`
  align-self: center;
  flex-grow: 1;
`
